import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Tooltip } from '../ui/tooltip';
import { useToast } from '../../hooks/use-toast';
import ConfirmChangesDialog from './ConfirmChangesDialog';
import AuditLogDisplay from './AuditLogDisplay';

export default function FieldPermissionsTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [changes, setChanges] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Fetch permission matrix
  const { data: matrixData, isLoading, error } = useQuery({
    queryKey: ['permissions', 'matrix'],
    queryFn: () => api.permissions.getMatrix()
  });

  // Mutation for saving changes
  const saveMutation = useMutation({
    mutationFn: (changesArray) => api.permissions.bulkUpdate(changesArray),
    onSuccess: () => {
      toast({
        title: t('common.success'),
        description: t('settings.permissions.saveSuccess'),
      });
      setChanges({});
      queryClient.invalidateQueries(['permissions']);
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: t('settings.permissions.saveError'),
        variant: 'destructive',
      });
      console.error('Save error:', error);
    }
  });

  const handleCheckboxChange = (fieldName, role, permission, value) => {
    const key = `${fieldName}_${role}`;
    const currentChange = changes[key] || {};

    // Get current permissions from matrix data
    const category = matrixData?.categories?.find(cat =>
      cat.fields.some(f => f.field_name === fieldName)
    );
    const field = category?.fields.find(f => f.field_name === fieldName);
    const currentPerms = field?.permissions[role] || { can_view: false, can_edit: false };

    // Calculate new values
    let newCanView = permission === 'can_view' ? value : (currentChange.can_view ?? currentPerms.can_view);
    let newCanEdit = permission === 'can_edit' ? value : (currentChange.can_edit ?? currentPerms.can_edit);

    // Validation: can_edit requires can_view
    if (permission === 'can_edit' && value === true && !newCanView) {
      // Auto-enable view when edit is enabled
      newCanView = true;
    } else if (permission === 'can_view' && value === false && newCanEdit) {
      // Auto-disable edit when view is disabled
      newCanEdit = false;
    }

    setChanges(prev => ({
      ...prev,
      [key]: {
        field_name: fieldName,
        role: role,
        can_view: newCanView,
        can_edit: newCanEdit
      }
    }));
  };

  const handleSave = () => {
    if (Object.keys(changes).length === 0) {
      toast({
        title: t('common.info'),
        description: t('settings.permissions.noChanges'),
      });
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirm = () => {
    const changesArray = Object.values(changes);
    saveMutation.mutate(changesArray);
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    setChanges({});
    toast({
      title: t('common.info'),
      description: t('settings.permissions.changesCancelled'),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-destructive">Error loading permissions: {error.message}</div>
      </div>
    );
  }

  if (!matrixData || !matrixData.categories || matrixData.categories.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">No permission data available</div>
      </div>
    );
  }

  const roles = ['TEACHER', 'INTERVIEWER', 'HOUSE_OWNER', 'PARENT', 'OFFICE'];
  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.permissions.title')}</CardTitle>
          <CardDescription>{t('settings.permissions.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Action buttons */}
          {hasChanges && (
            <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 p-4">
              <div className="text-sm text-blue-900 dark:text-blue-200">
                {t('settings.permissions.unsavedChanges', { count: Object.keys(changes).length })}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  {t('common.cancel')}
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? t('common.saving') : t('settings.permissions.saveChanges')}
                </Button>
              </div>
            </div>
          )}

          {/* Permission Matrix Table */}
          <div className="overflow-x-auto">
            {matrixData?.categories?.map(category => (
              <div key={category.name} className="mb-8">
                <h3 className="mb-4 text-lg font-semibold text-foreground">{t('settings.permissions.categories.' + category.name)}</h3>
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full border-collapse">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="border-b border-r border-border p-3 text-left text-sm font-semibold text-foreground">
                          {t('settings.permissions.fieldColumn')}
                        </th>
                        {roles.map(role => (
                          <th key={role} className="border-b border-border p-3 text-center text-sm font-semibold text-foreground">
                            <div>{t('settings.permissions.roles.' + role)}</div>
                            <div className="mt-1 text-xs font-normal text-muted-foreground">
                              {t('settings.permissions.viewLabel')} | {t('settings.permissions.editLabel')}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-background">
                      {category.fields.map((field, idx) => {
                        const isLastRow = idx === category.fields.length - 1;
                        return (
                          <tr
                            key={field.field_name}
                            className="hover:bg-muted/50 transition-colors"
                          >
                            <td className={`border-r border-border p-3 ${!isLastRow ? 'border-b' : ''}`}>
                              <div className="font-medium text-sm text-foreground">{t('settings.permissions.fields.' + field.field_name)}</div>
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                {t('settings.permissions.descriptions.' + field.field_name)}
                              </div>
                            </td>
                            {roles.map(role => {
                              const perm = field.permissions[role] || {};
                              const key = `${field.field_name}_${role}`;
                              const change = changes[key];
                              const canView = change ? change.can_view : perm.can_view;
                              const canEdit = change ? change.can_edit : perm.can_edit;
                              const hasFieldChange = !!change;
                              const isReadOnly = field.read_only;

                              // Determine Edit checkbox state and tooltip
                              const editDisabled = isReadOnly || !canView;
                              const editTooltip = isReadOnly
                                ? t('settings.permissions.fieldReadOnly')
                                : t('settings.permissions.editRequiresView');

                              return (
                                <td
                                  key={role}
                                  className={`p-3 text-center ${!isLastRow ? 'border-b border-border' : ''} ${hasFieldChange ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
                                >
                                  <div className="flex items-center justify-center gap-4">
                                    <Checkbox
                                      checked={canView}
                                      onCheckedChange={(val) =>
                                        handleCheckboxChange(field.field_name, role, 'can_view', val)
                                      }
                                    />
                                    {editDisabled ? (
                                      <Tooltip content={editTooltip} side="top">
                                        <Checkbox
                                          checked={canEdit}
                                          disabled
                                          className="opacity-30"
                                        />
                                      </Tooltip>
                                    ) : (
                                      <Checkbox
                                        checked={canEdit}
                                        onCheckedChange={(val) =>
                                          handleCheckboxChange(field.field_name, role, 'can_edit', val)
                                        }
                                      />
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {/* Fixed save button at bottom */}
          <div className="mt-6 flex justify-end border-t border-border pt-4">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              size="lg"
            >
              {saveMutation.isPending
                ? t('common.saving')
                : t('settings.permissions.saveChanges')
              }
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log */}
      <AuditLogDisplay />

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <ConfirmChangesDialog
          changes={changes}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}
    </div>
  );
}
